import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHookstate } from "@hookstate/core";
import {
    SITEID_GLOBAL_STATE
} from "globalStore/globalStore";
import { useTranslation } from "react-i18next";
import { PEOPLE_SERVICES } from "redux/reduxToolkitServices/_commonServices/peopleServices";
import '@progress/kendo-theme-default/dist/all.css';
import {
    ModalTitle,
    ModalButton,
    EditPersonTabs
} from "pages/People/PeopleStyled";
import ModalCloseIcon from "assets/svgIcons/ModalCloseIcon";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuItem from "@mui/material/MenuItem";
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import CheckIcon from "assets/svgIcons/PeopleSubSideNav/CheckIcon";
import PeopleAlertModal from "../peopleAlertModal";

const EditPersonModal = React.memo(({   groupsData, 
                                        rfidAccessLevelData,
                                        setIsLoading,
                                        selectedPerson,
                                        setSelectedPerson,
                                        editPersonModalOpen,
                                        setEditPersonModalOpen
 }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const [firstName, setFirstName] = useState(selectedPerson?.FirstName);
    const [lastName, setLastName] = useState(selectedPerson?.LastName);
    const [personIdentifier, setPersonIdentifier] = useState(selectedPerson?.Identifier);
    const [groupId, setGroupId] = useState(selectedPerson?.GroupId);
    const [rfidAccessLevelId, setRfidAccessLevelId] = useState(selectedPerson?.RFIDAccessLevelId);
    const [inactivedPerson, setInactivedPerson] = useState(selectedPerson?.Inactivated);
    const [isFirstNameValid, setIsFirstNameValid] = useState(true);
    const [isLastNameValid, setIsLastNameValid] = useState(true);
    const [editPersonTabSelected, setEditPersonTabSelected] = useState(0);
    const [shouldSaveEditButtonBeDisabled, setShouldSaveEditButtonBeDisabled] = useState(true);
    const [peopleModalOpen, setPeopleModalOpen] = useState(false);
    const [titlePeopleAlertModal, setTitlePeopleAlertModal] = useState("");
    const [contentPeopleAlertModal, setContentPeopleAlertModal] = useState("");
    const [peopleAlertModalIsMessageOnly, setPeopleAlertModalIsMessageOnly] = useState(false);

    useEffect(() => {
        if(selectedPerson) {
            setFirstName(selectedPerson.FirstName);
            setLastName(selectedPerson.LastName);
            setPersonIdentifier(selectedPerson.Identifier);
            setGroupId(selectedPerson.GroupId);
            setRfidAccessLevelId(selectedPerson.RFIDAccessLevelId);
            setInactivedPerson(selectedPerson.Inactivated)
        }
    }, [selectedPerson]);

    const isFirstNameValidation = (e) => {
        setIsFirstNameValid(e.target.value.length >= 2);
    }

    const isLastNameValidation = (e) => {
        setIsLastNameValid(e.target.value.length >= 2);
    }

    const openPeopleDiscardChangesModal = () => {
        if(selectedPerson.FirstName != firstName || 
         selectedPerson.LastName != lastName ||
         selectedPerson.Identifier != personIdentifier ||
         selectedPerson.GroupId != groupId ||
         selectedPerson.RFIDAccessLevelId != rfidAccessLevelId ||
         selectedPerson.Inactivated != inactivedPerson) {
            setTitlePeopleAlertModal(t("Confirm_cancel"));
            setContentPeopleAlertModal(t("Are_You_Sure_You_Want_To_Discard_Changes") + "?");
            setPeopleAlertModalIsMessageOnly(false);
            setPeopleModalOpen(true);
        } else {
            closeEditPersonModal();
        }
    }

    const closeEditPersonModal = () => {
        setSelectedPerson(null)
        setEditPersonModalOpen(false);
        setEditPersonTabSelected(0);

        setFirstName("");
        setLastName("");
        setPersonIdentifier("");
        setGroupId(null);
        setRfidAccessLevelId(1);
        setIsFirstNameValid(true);
        setIsLastNameValid(true);
        setShouldSaveEditButtonBeDisabled(true);

        setPeopleModalOpen(false);
    }

    const handleEditPersonTabSelect = (e) => {
        setEditPersonTabSelected(e.selected);
    }

    const editPerson = () => {
        if(selectedPerson.PersonId != null && firstName != "" && lastName != "") {            
            let request = {
                personId: selectedPerson.PersonId,
                firstName: firstName,
                lastName: lastName,
                identifier: personIdentifier,
                groupId: groupId,
                rfidAccessLevelId: rfidAccessLevelId,
                inactivated: inactivedPerson,
                RFIDTag: selectedPerson.RFIDTag,
                driverId: selectedPerson.DriverId
            };

            dispatch(PEOPLE_SERVICES.editPersonService(siteId.get(), selectedPerson.PersonId, request)).then((data) => {
                if(data.WarningMessage != null && data.WarningMessage != "") {
                    setTitlePeopleAlertModal(t("Message"));
                    setContentPeopleAlertModal(data.WarningMessage);
                    setPeopleAlertModalIsMessageOnly(true);
                    setPeopleModalOpen(true);
                }
            });

            closeEditPersonModal();
            setIsLoading(true);
        } else {
            setIsFirstNameValid(firstName.length >= 2);
            setIsLastNameValid(lastName.length >= 2);
        }
    }

    const closeAlertModal = () => {
        setPeopleModalOpen(false);
    }

    const editPersonModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        bgcolor: '#ffffff',
        border: '2px solid #ccc',
        boxShadow: 24,
        p: 4,
    };

    const editPersonContentStyle = {
        display: 'flex', 
        marginBottom: '10px', 
        border: '1px solid #ccc',
        padding: '8px',          
        borderRadius: '4px',
        justifyContent: 'space-between',
        width: '360px'
    }

    const tabStripStyle = {
        width: '400px',
        margin: '0 auto',
        backgroundColor: '#f0f0f0',
        borderRadius: '8px',
        color: '#000'
    }

    const tabStripTabStyle = {
        minWidth: '150px',
        padding: '12px 30px',
        fontSize: '16px',
        color: '#000'
    }

    const personInputStyle = {
        width: '100%', 
        height: 38, 
        border: 'solid 1px #ccc' 
    }

    return (<>
                <Modal 
                    open={editPersonModalOpen}
                    aria-labelledby="modal-title"
                    aria-describedby="modal-description">
                        <Box sx={editPersonModalStyle}>
                        <ModalTitle>
                                <div>{selectedPerson?.LastName}, {selectedPerson?.FirstName}</div>

                                <div style={{display: 'flex', justifyContent: 'flex-end', cursor: 'pointer'}} onClick={openPeopleDiscardChangesModal}>
                                    <ModalCloseIcon width={18} height={18} color="#fff" />
                                </div>
                            </ModalTitle> 

                            <EditPersonTabs>
                                <TabStrip style={tabStripStyle} selected={editPersonTabSelected} onSelect={handleEditPersonTabSelect}>
                                    <TabStripTab style={tabStripTabStyle} title={t("Information")}>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px' }}>Last Name</strong>
                                            <span>{selectedPerson?.LastName}</span>
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px' }}>First Name</strong>
                                            <span>{selectedPerson?.FirstName}</span>
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px' }}>Identifier</strong>
                                            
                                            <span>{selectedPerson?.Identifier}</span>
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px' }}>Group</strong>
                                            
                                            <span>{selectedPerson?.GroupName}</span>
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px' }}>RFID Access Level</strong>
                                            
                                            <span>{selectedPerson?.RFIDAccessLevel}</span>
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px' }}>Inactive</strong>
                                            
                                            <span>{selectedPerson?.Inactivated ? (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    height: '100%',
                                                                }}>
                                                                    <CheckIcon />
                                                                </div>
                                                            ) : null
                                                    }
                                            </span>
                                        </div>     
                                    </TabStripTab>
                                    <TabStripTab style={tabStripTabStyle} title={t("Settings")}>
                                        {!isLastNameValid && 
                                            <div style={{color: 'red', fontSize: 10}}>
                                                {(t("Last_Name_People_Validation"))}
                                            </div>
                                        }
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px', width: 130 }}>{t("Last_Name")}</strong>
                                            
                                            <input type="text" value={lastName} onBlur={isLastNameValidation} onChange={(e) => { setLastName(e.target.value); setShouldSaveEditButtonBeDisabled(false);}} style={personInputStyle} />
                                        </div>
                                        
                                        {!isFirstNameValid && 
                                            <div style={{color: 'red', fontSize: 10}}>
                                                {(t("First_Name_People_Validation"))}
                                            </div>
                                        }
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px', width: 130 }}>{t("First_Name")}</strong>
                                            
                                            <input type="text" value={firstName} onBlur={isFirstNameValidation} onChange={(e) => { setFirstName(e.target.value); setShouldSaveEditButtonBeDisabled(false);}} style={personInputStyle} />
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px', width: 130 }}>Identifier</strong>
                                            
                                            <input type="text" value={personIdentifier} onChange={(e) => { setPersonIdentifier(e.target.value); setShouldSaveEditButtonBeDisabled(false);}} style={personInputStyle} />
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px', width: 130 }}>Group</strong>
                                            
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={groupId}
                                                    name="group"
                                                    onChange={(e) => { setGroupId(e.target.value); setShouldSaveEditButtonBeDisabled(false);}}
                                                    IconComponent={ExpandMoreIcon}
                                                    placeholder="Select.."
                                                    displayEmpty
                                                    inputProps={{ "aria-label": "Without label" }}
                                                >
                                                    <MenuItem key={0} value={null}>
                                                        Select..
                                                    </MenuItem>

                                                    {groupsData.map((group) => (
                                                        <MenuItem key={group.GroupId} value={group.GroupId}>
                                                        {group.GroupName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px', width: 130 }}>RFID Access Level</strong>
                                            
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={rfidAccessLevelId}
                                                    name="rfidAccessLevel"
                                                    onChange={(e) => { setRfidAccessLevelId(e.target.value); setShouldSaveEditButtonBeDisabled(false);}}
                                                    IconComponent={ExpandMoreIcon}
                                                    displayEmpty
                                                    inputProps={{ "aria-label": "Without label" }}
                                                >
                                                    {rfidAccessLevelData.map((rfidAccessLevel) => (
                                                        <MenuItem key={rfidAccessLevel.Id} value={rfidAccessLevel.Id}>
                                                        {rfidAccessLevel.AccessLevel}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <div style={editPersonContentStyle}>
                                            <strong style={{ marginRight: '10px', width: 130 }}>Inactive</strong>
                                            
                                            <span><input type="checkbox" checked={inactivedPerson} onChange={(e) => { setInactivedPerson(e.target.checked); setShouldSaveEditButtonBeDisabled(false);}} /></span>
                                        </div>   
                                    </TabStripTab>
                                </TabStrip>       
                            </EditPersonTabs>                                                            

                            <ModalButton>
                                <>
                                {editPersonTabSelected == 0 && 
                                <button 
                                    onClick={closeEditPersonModal} 
                                    style={{ height: 35, padding: 10, backgroundColor: '#bc9b6a', color: 'white', border: 'solid 1px #ccc' }}>
                                        Done
                                </button>
                                }
                                {editPersonTabSelected == 1 && 
                                    <>
                                        <button 
                                            onClick={openPeopleDiscardChangesModal} 
                                            style={{ marginRight: 10, height: 35, padding: 10,  backgroundColor: '#ccc', color: 'white', border: 'solid 1px #ccc' }}>
                                                Cancel
                                        </button>
                                        <button 
                                            disabled={shouldSaveEditButtonBeDisabled}
                                            onClick={editPerson}
                                            style={shouldSaveEditButtonBeDisabled ? { height: 35, padding: 10,  backgroundColor: '#ccc', color: 'white', border: 'solid 1px #ccc', cursor: "not-allowed	"  } : { height: 35, padding: 10, backgroundColor: '#bc9b6a', color: 'white', border: 'solid 1px #ccc' }}>
                                                Save
                                        </button>
                                    </>
                                }
                                </>
                            </ModalButton>
                        </Box>
                </Modal>
                <PeopleAlertModal
                                 openPeopleAlertModalOpen={peopleModalOpen} 
                                 yesCallbackFunction={() => closeEditPersonModal()}
                                 noCallbackFunction={() => closeAlertModal()}
                                 title={titlePeopleAlertModal}
                                 content={contentPeopleAlertModal} 
                                 loading={false}
                                 isMessageOnly={peopleAlertModalIsMessageOnly} />
    </>
    );
});

export default EditPersonModal;
